import React, { useCallback, useEffect, useState, useMemo } from "react";
import { ModeBadge } from "./ModeBadge";
import axios from "axios";
import {
  Activity, Bookmark, Check, ChevronDown, ChevronRight,
  Clock, History, BellOff, Bell, RefreshCw, Zap, Power, ShieldOff, Download,
} from "lucide-react";
import { Skeleton } from "./ui/skeleton";
import { CollapseToggle } from "./CollapseToggle";
import { useCollapsed } from "../hooks/useCollapsed";
import { ExecutionJourney } from "./ExecutionJourney";
import { RejectionSummaryPanel } from "./RejectionSummaryPanel";
import { useAuth } from "../auth/AuthContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n, d = 2) =>
  n == null
    ? "—"
    : Number(n).toLocaleString("en-IN", { maximumFractionDigits: d });

const toIST = (utcIso) => {
  if (!utcIso) return "—";
  try {
    let s = String(utcIso);
    if (!/[zZ]|[+-]\d{2}:?\d{2}$/.test(s)) s = s + "Z";
    const d = new Date(s);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleTimeString("en-GB", {
      timeZone: "Asia/Kolkata",
      hour12: false,
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return "—";
  }
};

// Phase 26: full date+time IST for trade history (e.g. "06 Jun 14:32 IST")
const toISTDateTime = (utcIso) => {
  if (!utcIso) return "—";
  try {
    let s = String(utcIso);
    if (!/[zZ]|[+-]\d{2}:?\d{2}$/.test(s)) s = s + "Z";
    const d = new Date(s);
    if (isNaN(d.getTime())) return "—";
    const fmt = new Intl.DateTimeFormat("en-GB", {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
    return fmt.format(d).replace(",", "");
  } catch {
    return "—";
  }
};

// 2026-08 — why a "qualifying" pick didn't turn into a paper trade. Backed by
// `auto_entry_log` (candle_breakout_auto_entry_log), populated at every skip
// point in `_auto_enter_top_pick` — cutoff, daily cap, missing option data,
// dedup, contract quality, spread, chase guard, price-move gate, R:R floor.
const AUTO_ENTRY_SKIP_LABELS = {
  past_cutoff: "Past the 14:00 IST cutoff for new auto-entries",
  daily_cap_reached: "Daily auto-entry cap already reached",
  no_option_strike: "No ATM option strike/LTP (Upstox token expired?)",
  already_auto_entered_today: "This symbol already auto-entered earlier today",
  contract_quality: "Contract quality gate failed (illiquid / low OI)",
  wide_spread: "Bid-ask spread too wide",
  option_already_chased: "ATM Call already moved too far from today's open",
  price_move_gate: "Option premium moved too far since first qualifying",
  risk_reward_below_min: "Risk:Reward below the configured floor",
};

const AutoEntrySkipLog = ({ rows }) => {
  const [collapsed, setCollapsed] = useState(false);
  if (!rows || rows.length === 0) return null;
  return (
    <div className="border-grid surface" data-testid="candle-breakout-auto-entry-log">
      <div className="px-4 py-2 border-b border-grid text-[10px] uppercase tracking-wider font-mono-data text-muted-3 flex items-center justify-between gap-2">
        <span>Why no auto-entry? · {rows.length} skipped today</span>
        <button
          data-testid="candle-breakout-auto-entry-log-toggle"
          onClick={() => setCollapsed((v) => !v)}
          className="text-[10px] uppercase tracking-wider font-mono-data px-2 py-0.5 border border-grid hover:bg-white/5"
        >
          {collapsed ? "Show" : "Hide"}
        </button>
      </div>
      {/* 2026-10-30 — chat: "Implement scrollbar for CB...it's too lengthy": the
          log grows by several rows every 5-min slot, so cap the height (~10 rows)
          and scroll inside, header pinned. */}
      {!collapsed && (
        <div className="max-h-[420px] overflow-y-auto" data-testid="candle-breakout-auto-entry-log-scroll">
        <table className="w-full text-xs" data-testid="candle-breakout-auto-entry-log-table">
          <thead className="sticky top-0 z-10" style={{ background: "var(--surface)" }}>
            <tr className="border-b border-grid text-[10px] uppercase tracking-wider text-muted-3 font-mono-data">
              <th className="text-left px-4 py-2">Time IST</th>
              <th className="text-left px-4 py-2">Slot</th>
              <th className="text-left px-4 py-2">Symbol</th>
              <th className="text-left px-4 py-2">Reason</th>
              <th className="text-left px-4 py-2">Detail</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr
                key={`${r.slot}__${r.symbol}__${i}`}
                data-testid={`candle-breakout-auto-entry-log-row-${i}`}
                className="border-b border-grid last:border-b-0 hover:bg-white/[0.02]"
              >
                <td className="px-4 py-2 font-mono-data text-muted-2 whitespace-nowrap">
                  {toIST(r.created_at)}
                </td>
                <td className="px-4 py-2 font-mono-data text-muted-3">{r.slot || "—"}</td>
                <td className="px-4 py-2 font-semibold">{r.symbol || "—"}</td>
                <td
                  className="px-4 py-2 font-mono-data"
                  style={{ color: "var(--bear)" }}
                  title={AUTO_ENTRY_SKIP_LABELS[r.reason] || r.reason}
                >
                  {AUTO_ENTRY_SKIP_LABELS[r.reason] || r.reason}
                </td>
                <td className="px-4 py-2 text-muted-2">{r.detail || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
      <div className="px-4 py-2 border-t border-grid text-[10px] font-mono-data text-muted-3">
        A "qualifying" pick above still has to clear these gates before it becomes a paper
        (or live) trade — this is why the count can be zero even with qualifying picks showing.
      </div>
    </div>
  );
};

const SignedPct = ({ value }) => {
  if (value == null) return <span className="text-muted-3">—</span>;
  const pos = value >= 0;
  return (
    <span style={{ color: pos ? "var(--bull)" : "var(--bear)" }} className="font-mono-data">
      {pos ? "+" : ""}
      {fmt(value)}%
    </span>
  );
};

const ProfitPill = ({ value }) => {
  if (value == null) return <span className="text-muted-3">—</span>;
  const sweet = value >= 10 && value <= 15;
  const above = value >= 8;
  const color = sweet ? "var(--bull)" : above ? "#FFB66E" : "var(--text-muted)";
  return (
    <span
      className="inline-flex items-center gap-1 font-mono-data px-1.5 py-0.5 border"
      style={{ color, borderColor: color }}
    >
      {sweet && <Zap size={11} />}
      ~{fmt(value)}%
    </span>
  );
};

const LossPill = ({ value }) => {
  if (value == null) return <span className="text-muted-3">—</span>;
  const color = "var(--bear)";
  return (
    <span
      className="inline-flex items-center gap-1 font-mono-data px-1.5 py-0.5 border"
      style={{ color, borderColor: color }}
      data-testid="candle-breakout-loss-pill"
    >
      ~{fmt(value)}%
    </span>
  );
};

// 2026-10-07 — real +8.5%/-10% exit vs a shadow trailing exit (backend
// cb_shadow_exit.py: -40% hard stop; once +10%, exit on a 30% give-back of
// the peak; 15:15 EOD). Log only — nothing is sold by the shadow.
const ShadowExitCard = () => {
  const [d, setD] = useState(null);
  useEffect(() => {
    axios.get(`${API}/candle-breakout/shadow-exit-summary`, { params: { days: 30 } })
      .then((r) => setD(r.data)).catch(() => {});
  }, []);
  if (!d) return null;
  const f = (v, s = "") => (v == null ? "—" : `${v}${s}`);
  const Row = ({ label, st }) => (
    <tr className="border-b border-grid/40">
      <td className="px-3 py-1.5 text-muted-2">{label}</td>
      <td className="px-3 py-1.5">{f(st?.n)}</td>
      <td className="px-3 py-1.5">{f(st?.win_rate_pct, "%")}</td>
      <td className="px-3 py-1.5" style={{ color: (st?.avg_pct ?? 0) >= 0 ? "var(--bull)" : "var(--bear)" }}>
        {st?.avg_pct == null ? "—" : `${st.avg_pct >= 0 ? "+" : ""}${st.avg_pct}%`}
      </td>
      <td className="px-3 py-1.5">{f(st?.profit_factor)}</td>
      <td className="px-3 py-1.5 text-muted-2">{f(st?.avg_loss_pct, "%")} / {f(st?.worst_pct, "%")}</td>
    </tr>
  );
  return (
    <div className="border border-grid p-3 space-y-2" data-testid="cb-shadow-exit-card">
      <div className="text-[10px] uppercase tracking-[0.2em] text-muted-2 font-mono-data">
        Exit test · real vs shadow trailing exit (last 30 days)
      </div>
      <div className="text-[10px] text-muted-3 font-mono-data">
        Log only. Shadow: −40% safety stop; once the option is up +10%, exit if it gives back 30% of its best gain; 15:15 EOD.
        Same trades, real option prices. Switch only if the shadow clearly beats the real exit over ~30 trades.
      </div>
      {!d.real?.n ? (
        <div className="text-xs text-muted-3 font-mono-data">No completed comparisons yet — fills in as trades close.</div>
      ) : (
        <table className="w-full text-[11px] font-mono-data">
          <thead className="text-muted-3 text-[9px] uppercase tracking-[0.15em]">
            <tr className="border-b border-grid">
              {["Exit", "Trades", "Win rate", "Avg / trade", "Profit factor", "Avg loss / worst"].map((h) => (
                <th key={h} className="text-left px-3 py-1.5 font-normal">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            <Row label="Real (+8.5% / −10%)" st={d.real} />
            <Row label="Shadow trailing" st={d.shadow} />
            {/* 2026-10-31 — out-of-sample week before switching (see cb_shadow_exit.CHECK_FROM) */}
            <tr><td colSpan={6} className="px-3 pt-3 pb-1 text-[9px] uppercase tracking-[0.15em] text-muted-3">
              Check week · trades since {d.check_from} (decision after ~1 week)
            </td></tr>
            <Row label="Real" st={d.real_check} />
            <Row label="Shadow trailing" st={d.shadow_check} />
          </tbody>
        </table>
      )}
      {/* 2026-11-04 — chat: "change TP to 5%?" -> shadow +5% target, log only */}
      <div className="text-[10px] uppercase tracking-[0.2em] text-muted-2 font-mono-data pt-2">
        Target test · real vs shadow +5% target (trades from 5 Nov)
      </div>
      {!d.tp5?.n ? (
        <div className="text-xs text-muted-3 font-mono-data">No tracked trades yet — every CB trade opened from now on is checked for a +5% touch while it is open.</div>
      ) : (
        <table className="w-full text-[11px] font-mono-data" data-testid="cb-tp5-card">
          <tbody>
            <Row label="Real exit" st={d.tp5.real} />
            <Row label={`Shadow +5% (touched ${d.tp5.touched}/${d.tp5.n})`} st={d.tp5.tp5} />
          </tbody>
        </table>
      )}
    </div>
  );
};

export const CandleBreakout = () => {
  const { isAdmin } = useAuth();
  const [data, setData] = useState(null);
  const [trades, setTrades] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [alertsPaused, setAlertsPaused] = useState(false);
  const [upstoxConnected, setUpstoxConnected] = useState(true);
  const [liveEnabled, setLiveEnabled] = useState(true);
  const [liveBusy, setLiveBusy] = useState(false);
  const [pauseBusy, setPauseBusy] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [collapsed, toggle] = useCollapsed("candle-breakout", false);
  // Phase 49b — Per-table Show/Hide toggle
  const [picksCollapsed, togglePicks] = useCollapsed("candle-breakout.picks", false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [picksRes, tradesRes, alertsRes, upstoxRes, liveRes] = await Promise.all([
        axios.get(`${API}/candle-breakout`),
        axios.get(`${API}/candle-breakout/paper-trades`),
        axios.get(`${API}/alerts/status`).catch(() => ({ data: {} })),
        axios.get(`${API}/upstox/status`).catch(() => ({ data: {} })),
        axios.get(`${API}/candle-breakout/live-status`).catch(() => ({ data: { enabled: true } })),
      ]);
      setData(picksRes.data);
      setTrades(tradesRes.data);
      setAlertsPaused(!!alertsRes.data?.paused);
      setUpstoxConnected(!!upstoxRes.data?.connected);
      setLiveEnabled(liveRes.data?.enabled !== false);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, [load]);

  const onScan = async () => {
    setScanning(true);
    try {
      await axios.post(`${API}/candle-breakout/scan`);
      toast.success("Scan complete — new picks added");
      await load();
    } catch {
      toast.error("Scan failed. Is Upstox connected?");
    } finally {
      setScanning(false);
    }
  };

  const onPaperTrade = async (slot, symbol) => {
    try {
      const r = await axios.post(`${API}/candle-breakout/paper-trade`, { slot, symbol });
      if (r.data?.ok === false && r.data?.reason === "open_trade_exists") {
        toast.error(
          r.data.message || `Another trade is open (${r.data.open_symbol}). Only one at a time.`
        );
        return;
      }
      toast.success(`${symbol} added to paper trades`);
      await load();
    } catch {
      toast.error("Failed to mark paper trade");
    }
  };

  const onReconnectUpstox = async () => {
    try {
      const r = await axios.get(`${API}/upstox/auth-url`);
      if (r.data?.auth_url) window.location.href = r.data.auth_url;
    } catch {
      toast.error("Unable to start Upstox login.");
    }
  };

  const onTogglePause = async () => {
    setPauseBusy(true);
    try {
      const next = !alertsPaused;
      await axios.post(`${API}/alerts/pause`, null, {
        params: { paused: next ? "true" : "false" },
      });
      setAlertsPaused(next);
      toast.success(next ? "Telegram alerts paused" : "Telegram alerts resumed");
    } catch {
      toast.error("Failed to update alert status");
    } finally {
      setPauseBusy(false);
    }
  };

  const onToggleLive = async () => {
    const next = !liveEnabled;
    // Confirm before flipping live trading
    const verb = next ? "RESUME" : "PAUSE";
    if (!window.confirm(
      `${verb} LIVE trading?\n\n` +
      (next
        ? "New auto-entries will place real Upstox BUY orders again."
        : "New auto-entries will be SKIPPED (no new live BUYs).\n" +
          "Existing OPEN live positions will still be SOLD on TP/SL/EOD.")
    )) return;
    setLiveBusy(true);
    try {
      await axios.post(`${API}/candle-breakout/live-status`, { enabled: next });
      setLiveEnabled(next);
      toast.success(next ? "LIVE trading RESUMED" : "LIVE trading PAUSED — new buys blocked");
    } catch {
      toast.error("Failed to update live-trading flag");
    } finally {
      setLiveBusy(false);
    }
  };

  // Phase 24: per-trade Manual Exit. Single button — backend records the
  // close_reason as "MANUAL_EXIT" without classifying as profit/loss.
  const onManualExit = async (tradeId, symbol) => {
    if (!window.confirm(`Manually exit ${symbol}?\n\nThis will close the trade at the latest LTP. Live trades will trigger a SELL on Upstox (only if a position still exists).`)) return;
    try {
      const r = await axios.post(`${API}/candle-breakout/manual-exit`, {
        trade_id: tradeId,
        action: "MANUAL_EXIT",
      });
      const trade = r?.data?.trade;
      const pnl = trade?.final_pnl_pct;
      const skipped = trade?.sell_skipped_reason === "no_position";
      toast.success(
        `${symbol}: Manual exit${pnl != null ? ` · P&L ${pnl >= 0 ? "+" : ""}${pnl}%` : ""}` +
        (skipped ? " · sell skipped (no Upstox position)" : "")
      );
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Manual exit failed";
      toast.error(`${symbol}: ${detail}`);
    }
  };

  // Flatten runs → single row list (newest first), each row carries its slot.
  const rows = useMemo(() => {
    // data.runs is sorted newest-first by the backend. Dedupe by symbol so the
    // table shows ONE row per stock with the LATEST scan values (skips repeats
    // from earlier slots).
    const out = [];
    const seen = new Set();
    for (const run of data?.runs || []) {
      for (const p of run.picks || []) {
        const sym = (p?.symbol || "").toUpperCase();
        if (!sym || seen.has(sym)) continue;
        seen.add(sym);
        out.push({
          ...p,
          slot: run.slot,
          ran_at_utc: run.ran_at_utc,
          ran_at_ist: run.ran_at_ist,
          chartink_total: run.chartink_total,
          manual: run.manual,
        });
      }
    }
    return out;
  }, [data]);

  // Trade-status map keyed by `${slot}__${symbol}` so each row knows:
  //   - whether it's paper or live
  //   - the confirmed broker status (open / complete / rejected / etc.)
  const paperMap = new Map(
    (data?.paper_trades || []).map((t) => [
      `${t.slot}__${t.symbol}`,
      {
        live: !!t.live_trade,
        status: (t.live_broker_status || "").toLowerCase(),
        order_id: t.live_buy_order_id,
        avg_fill: t.live_avg_fill_price,
        filled_qty: t.live_filled_qty,
      },
    ])
  );

  return (
    <section className="space-y-4" data-testid="candle-breakout-section">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-muted-3 font-mono-data mb-1">
            05 / Auto-Pilot
          </div>
          <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight">
            Candle Breakout · 5-Min Live-Pilot
          </h2>
          <p className="text-xs text-muted-2 mt-1 max-w-3xl" data-testid="candle-breakout-description">
              <strong style={{ color: "var(--bull)" }}>LIVE trading</strong> · Every
              5 min from 09:30 to 12:00 IST we run the native 2-candle breakout
              scan, pick the best stock by ATM Call anticipated profit, and place a
              real <strong>Upstox BUY LIMIT</strong> at LTP −5% (1 lot, delivery/NRML).
              <strong> Only stocks with Multi-TF 3/3</strong> (5m + 1h + 1d all bullish) qualify.
              Daily <strong>SuperTrend(7,3)</strong> + <strong>RSI(14) ≥ 50</strong>{" "}
              gates layered on top (admin-overridable from the Configuration panel).
              Up to <strong>{data?.max_auto_picks_per_day ?? 2} trades/day</strong>{" "}
              (parallel allowed, <strong>live trades only</strong> — paper
              auto-entry is unlimited; change the cap from{" "}
              <strong>Admin → Configuration → Section 5</strong>).{" "}
              <strong style={{ color: "var(--bear)" }}>No new buys after 14:00 IST.</strong>{" "}
              <strong>Dynamic target +6% to +10%</strong> & SL{" "}
              <strong style={{ color: "var(--bear)" }}>-10% to -20%</strong> per stock
              (sector + market + ATR).{" "}
              <strong>R:R ≥ {(1.0).toFixed(1)}:1</strong> auto-entry gate
              (admin-configurable via <code>section5.min_risk_reward</code>).
              Auto-SELLs at LTP +0.25% on TP/SL hit or 15:15 IST EOD.
              <strong> Option chase guard</strong>: skips ATM Call strikes already up ≥50% from today's open.
            </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {data?.next_scan_at && (
            <span className="text-[10px] uppercase tracking-wider font-mono-data px-2 py-1 border border-grid text-muted-2">
              Next · {data.next_scan_at}
            </span>
          )}
          <button
            data-testid="candle-breakout-live-toggle-btn"
            onClick={onToggleLive}
            disabled={liveBusy}
            // 2026-09-12 — "apply everywhere" follow-up to the VWAP Bounce
            // A/B/C live-toggle highlight fix (chat: "B in this case, isn't
            // looking good"). ON previously differed from OFF only by
            // border/text color on the same flat surface bg; ON now also
            // gets the bull-tinted wash so it reads as active, not just
            // differently colored.
            className="border-grid px-3 py-1.5 text-[10px] uppercase tracking-wider font-mono-data hover:bg-white/5 disabled:opacity-50 inline-flex items-center gap-1.5"
            style={{
              color: liveEnabled ? "var(--bull)" : "var(--bear)",
              borderColor: liveEnabled ? "var(--bull)" : "var(--bear)",
              background: liveEnabled ? "var(--bull-muted)" : "var(--surface)",
            }}
            title={liveEnabled
              ? "LIVE trading ACTIVE — click to pause new buys"
              : "LIVE trading PAUSED — click to resume new buys"}
          >
            {liveEnabled ? <Power size={12} /> : <ShieldOff size={12} />}
            {liveEnabled ? "Live · ON" : "Live · OFF"}
          </button>
          <button
            data-testid="candle-breakout-pause-alerts-btn"
            onClick={onTogglePause}
            disabled={pauseBusy}
            className="border-grid surface px-3 py-1.5 text-[10px] uppercase tracking-wider font-mono-data hover:bg-white/5 disabled:opacity-50 inline-flex items-center gap-1.5"
            style={{
              color: alertsPaused ? "var(--bear)" : "var(--bull)",
              borderColor: alertsPaused ? "var(--bear)" : "var(--border-color)",
            }}
            title={alertsPaused ? "Telegram alerts paused — click to resume" : "Click to pause Telegram alerts"}
          >
            {alertsPaused ? <BellOff size={12} /> : <Bell size={12} />}
            {alertsPaused ? "Alerts Paused" : "Alerts On"}
          </button>
          <button
            data-testid="candle-breakout-scan-btn"
            onClick={onScan}
            disabled={scanning}
            className="border-grid surface px-3 py-1.5 text-[10px] uppercase tracking-wider font-mono-data hover:bg-white/5 disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            <RefreshCw size={12} className={scanning ? "animate-spin" : ""} />
            {scanning ? "Scanning…" : "Run Scan Now"}
          </button>
          <CollapseToggle collapsed={collapsed} onToggle={toggle} testId="candle-breakout-toggle" />
        </div>
      </div>

      {!collapsed && (
        <>
          {/* Upstox disconnected banner */}
          {!upstoxConnected && (
            <div
              data-testid="cb-upstox-banner"
              className="border surface px-4 py-3 flex items-center justify-between gap-3"
              style={{ borderColor: "var(--bear)", background: "rgba(255,80,80,0.08)" }}
            >
              <div className="text-xs">
                <strong style={{ color: "var(--bear)" }}>Upstox token expired.</strong>{" "}
                <span className="text-muted-2">
                  Option strikes, live P&amp;L and EOD auto-close need a fresh
                  token. Configure auto-login below to refresh it daily without a tap.
                </span>
              </div>
              <button
                data-testid="cb-reconnect-upstox-btn"
                onClick={onReconnectUpstox}
                className="px-3 py-1.5 text-[10px] uppercase tracking-wider font-mono-data border"
                style={{ borderColor: "var(--bear)", color: "var(--bear)" }}
              >
                Reconnect Upstox
              </button>
            </div>
          )}

          {/* 2026-08-25 — moved to Settings (UserPages.jsx), where it's
              actually visible and reachable; was rendered hidden={true}
              here before, meaning nobody could ever configure real
              auto-login. See UpstoxAutoLoginPanel.jsx for the full story. */}

          {/* Phase 38: Show scan window range instead of the full 37-slot list */}
          <div className="border-grid surface px-4 py-2.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px] uppercase tracking-wider font-mono-data">
            <span className="inline-flex items-center gap-1.5 text-muted-2">
              <Clock size={11} className="text-muted-3" />
              <span className="text-muted-3">Scan window IST · </span>
              <strong style={{ color: "var(--text-primary)" }}>
                {(data?.scan_hours_ist || [])[0] || "—"}
              </strong>
              <span className="text-muted-3">→</span>
              <strong style={{ color: "var(--text-primary)" }}>
                {(data?.scan_hours_ist || []).slice(-1)[0] || "—"}
              </strong>
              <span className="text-muted-3">
                {/* 2026-09-03 — was a hardcoded "@ 5 min"; scan cadence
                    lowered to 10 min (chat request) and scan_hours_ist is
                    now backend-filtered to the actual firing slots, so this
                    reads the real interval from the API instead of a
                    literal that would silently go stale again next time
                    the cadence changes. */}
                · {(data?.scan_hours_ist || []).length || 0} slots @ {data?.scan_interval_min ?? 10} min
              </span>
            </span>
            <span className="ml-auto text-muted-3">
              Picks · {rows.length} · Auto today · {data?.auto_today_count ?? 0}/{data?.max_auto_picks_per_day ?? 2} ·{" "}
              <span style={{ color: "var(--bull)" }}>TP +8 to +14%</span> ·{" "}
              <span style={{ color: "var(--bear)" }}>SL -10 to -20%</span> · EOD {data?.eod_close_at || "15:15 IST"}
            </span>
          </div>

          {loading && !data ? (
            <div className="border-grid surface p-4 space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : rows.length === 0 ? (
            <>
              <div className="border-grid surface p-6 text-center">
                <Activity size={20} className="mx-auto mb-2 text-muted-3" />
                <p className="text-sm text-muted-2">
                  No scans yet today. Scans run every 5 minutes between 09:30 and 12:30
                  IST on weekdays — or hit "Run Scan Now" to fire one manually.
                </p>
              </div>
              <RejectionSummaryPanel
                testid="candle-breakout-rejection-summary"
                title="Why no breakouts?"
                slot={data?.runs?.[0]?.slot}
                universeSize={data?.runs?.[0]?.universe_size}
                rejectionSummary={data?.runs?.[0]?.rejection_summary || {}}
              />
            </>
          ) : (
            <div className="border-grid surface overflow-x-auto">
              <div className="px-4 py-2 border-b border-grid text-[10px] uppercase tracking-wider font-mono-data text-muted-3 flex items-center justify-between gap-2">
                <span>Today&apos;s Qualifying Picks · {rows.length}</span>
                <CollapseToggle
                  collapsed={picksCollapsed}
                  onToggle={togglePicks}
                  testId="candle-breakout-picks-toggle"
                />
              </div>
              {!picksCollapsed && (
              <table className="w-full text-xs" data-testid="candle-breakout-table">
                <thead>
                  <tr className="border-b border-grid text-[10px] uppercase tracking-wider text-muted-3 font-mono-data">
                    <th className="text-left px-4 py-2 w-12">Sr.</th>
                    <th className="text-left px-4 py-2">Time IST</th>
                    <th className="text-left px-4 py-2">Slot</th>
                    <th className="text-left px-4 py-2">Symbol</th>
                    <th className="text-right px-4 py-2">Close</th>
                    <th className="text-right px-4 py-2">% Change</th>
                    <th className="text-right px-4 py-2">Volume</th>
                    <th className="text-right px-4 py-2">Strike</th>
                    <th className="text-right px-4 py-2">CE LTP</th>
                    <th className="text-right px-4 py-2">Δ</th>
                    <th className="text-right px-4 py-2">ATR(14)</th>
                    <th className="text-center px-4 py-2" title="Multi-Timeframe agreement — 5m + 1h + 1d. Section 5 requires 3/3 (all timeframes bullish).">Multi-TF</th>
                    <th className="text-right px-4 py-2" title="Dynamic profit target — sector + market + ATR">TP %</th>
                    <th className="text-right px-4 py-2" title="Dynamic stop-loss — sector + market + ATR">SL %</th>
                    <th className="text-right px-4 py-2 w-32">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => {
                    const key = `${r.slot}__${r.symbol}`;
                    const trade = paperMap.get(key);
                    const marked = !!trade;
                    // Status pill semantics:
                    //   live + status in {open,complete,trigger pending} → green LIVE
                    //   live + status rejected/cancelled → red REJECTED
                    //   live + status timeout/unknown → amber PENDING
                    //   paper → blue PAPER
                    let badge = null;
                    if (marked) {
                      if (trade.live) {
                        if (["complete"].includes(trade.status)) {
                          badge = { label: "Live · Filled", color: "var(--bull)" };
                        } else if (["open", "trigger pending"].includes(trade.status)) {
                          badge = { label: "Live · Open", color: "var(--bull)" };
                        } else if (["rejected", "cancelled", "cancel pending"].includes(trade.status)) {
                          badge = { label: `Rejected`, color: "var(--bear)" };
                        } else {
                          badge = { label: "Live · Pending", color: "#FFB66E" };
                        }
                      } else {
                        badge = { label: "Auto-Paper", color: "var(--accent-blue)" };
                      }
                    }
                    const isManual = r.manual;
                    const slotShort = (r.slot || "").replace(/ IST$/, "").split(" ").slice(-1)[0];
                    return (
                      <tr
                        key={key + "_" + i}
                        data-testid={`candle-breakout-row-${i}`}
                        className="border-b border-grid last:border-b-0 hover:bg-white/[0.02]"
                      >
                        <td className="px-4 py-2 font-mono-data text-muted-3">{i + 1}</td>
                        <td className="px-4 py-2 font-mono-data text-muted-2">
                          {toIST(r.ran_at_utc)}
                        </td>
                        <td className="px-4 py-2 font-mono-data text-muted-2">
                          {isManual ? (
                            <span className="text-[9px] px-1 border border-grid">MANUAL</span>
                          ) : (
                            slotShort
                          )}
                        </td>
                        <td className="px-4 py-2 font-mono-data font-semibold">
                          {r.symbol}
                          {r.name && (
                            <div className="text-[10px] text-muted-3 font-normal">
                              {r.name}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-2 text-right font-mono-data">₹{fmt(r.close)}</td>
                        <td className="px-4 py-2 text-right">
                          <SignedPct value={r.pct_change} />
                        </td>
                        <td className="px-4 py-2 text-right font-mono-data">{fmt(r.volume, 0)}</td>
                        <td className="px-4 py-2 text-right font-mono-data font-semibold">
                          {r.atm_strike ? `${r.atm_strike} ${r.option_type || "CE"}` : <span className="text-muted-3">—</span>}
                        </td>
                        <td className="px-4 py-2 text-right font-mono-data">
                          {r.atm_ce_ltp ? `₹${fmt(r.atm_ce_ltp)}` : <span className="text-muted-3">—</span>}
                        </td>
                        <td className="px-4 py-2 text-right font-mono-data text-muted-2">
                          {r.delta ? fmt(r.delta, 2) : <span className="text-muted-3">—</span>}
                        </td>
                        <td className="px-4 py-2 text-right font-mono-data text-muted-2">
                          {r.atr14 ? `₹${fmt(r.atr14)}` : <span className="text-muted-3">—</span>}
                        </td>
                        <td className="px-4 py-2 text-center">
                          {(() => {
                            const mtfCount = r?.multi_tf?.bullish_count;
                            if (mtfCount == null) return <span className="text-muted-3">—</span>;
                            const color = mtfCount === 3 ? "var(--bull)" : mtfCount === 2 ? "#FFB66E" : "var(--text-muted)";
                            return (
                              <span
                                className="inline-flex items-center gap-1 font-mono-data text-[10px] px-1.5 py-0.5 border"
                                style={{ color, borderColor: color }}
                                title={`5m: ${r?.multi_tf?.tf_5m?.trend || '—'} · 1h: ${r?.multi_tf?.tf_1h?.trend || '—'} · 1d: ${r?.multi_tf?.tf_daily?.trend || '—'}`}
                              >
                                <Check size={11} /> {mtfCount}/3
                              </span>
                            );
                          })()}
                        </td>
                        <td className="px-4 py-2 text-right">
                          <ProfitPill value={r.target_pct ?? r.anticipated_profit_pct} />
                        </td>
                        <td className="px-4 py-2 text-right" title={r.levels_breakdown ? `Sector ${r.sector?.trend || '—'} · Market ${r.market_sentiment || '—'} · ATR ${r.atr_pct ?? '—'}%` : undefined}>
                          <LossPill value={r.stop_loss_pct ?? r.anticipated_loss_pct} />
                        </td>
                        <td className="px-4 py-2 text-right">
                          {marked && badge ? (
                            <span
                              data-testid={`candle-breakout-paper-marked-${i}`}
                              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-mono-data px-2 py-1 border"
                              style={{ color: badge.color, borderColor: badge.color }}
                              title={
                                trade.live
                                  ? `Order ID: ${trade.order_id || '—'}\nBroker status: ${trade.status || 'unknown'}\nFilled: ${trade.filled_qty || 0} @ ₹${trade.avg_fill || '—'}`
                                  : "Paper trade — no real order placed"
                              }
                            >
                              <Check size={11} /> {badge.label}
                            </span>
                          ) : (
                            <button
                              data-testid={`candle-breakout-paper-btn-${i}`}
                              onClick={() => onPaperTrade(r.slot, r.symbol)}
                              title="Manually mark as paper trade"
                              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-mono-data px-2 py-1 border border-grid hover:bg-white/5 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              <Bookmark size={11} /> Mark
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              )}
              <div className="px-4 py-2 border-t border-grid text-[10px] font-mono-data text-muted-3">
                {rows.length} picks · sorted newest first · click "Mark" to add to paper trades
              </div>
            </div>
          )}

          {/* Why no auto-entry? (2026-08 observability fix) */}
          <AutoEntrySkipLog rows={data?.auto_entry_log} />

          {/* 2026-10-07 — shadow trailing exit vs real exit (log only) */}
          <ShadowExitCard />

          {/* Paper Trade History (2-day, collapsible) */}
          <PaperTradeHistory
            trades={trades}
            open={historyOpen}
            onToggle={() => setHistoryOpen((v) => !v)}
            onManualExit={onManualExit}
          />
        </>
      )}
    </section>
  );
};


const PaperHistoryRow = ({ t, i, day, onManualExit }) => {
  const [expanded, setExpanded] = useState(false);
  const closed = t.status !== "OPEN";
  const pnl = closed ? (t.final_pnl_pct ?? t.pnl_pct) : t.pnl_pct;
  const closeLtp = closed ? (t.close_ce_ltp ?? t.current_ce_ltp) : t.current_ce_ltp;
  const color = pnl == null
    ? "var(--text-muted)"
    : pnl >= 0 ? "var(--bull)" : "var(--bear)";
  const hasJourney = Boolean(
    t.momentum_tier || t.buy_buffer_pct_actual || t.chase_status || t.live_buy_order_id
  );
  return (
    <React.Fragment>
      <tr
        data-testid={`paper-history-row-${day}-${i}`}
        className="border-b border-grid last:border-b-0 hover:bg-white/[0.02]"
      >
        <td className="px-4 py-2 font-mono-data text-muted-3">
          <div className="inline-flex items-center gap-1">
            {hasJourney && (
              <button
                onClick={() => setExpanded((v) => !v)}
                aria-expanded={expanded}
                aria-label={expanded ? "Hide Execution Journey" : "Show Execution Journey"}
                data-testid={`execution-journey-toggle-${day}-${i}`}
                className="text-muted-2 hover:text-primary"
                title="Toggle Execution Journey"
              >
                {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
              </button>
            )}
            <span>{i + 1}</span>
          </div>
        </td>
        <td className="px-4 py-2 font-mono-data text-muted-2">
          {toISTDateTime(t.entered_at)}
        </td>
        <td className="px-4 py-2 font-mono-data font-semibold">{t.symbol}<ModeBadge t={t} /></td>
        <td className="px-4 py-2 text-right font-mono-data">
          {t.atm_strike || "—"} {t.atm_strike ? "CE" : ""}
        </td>
        <td className="px-4 py-2 text-right font-mono-data">
          {t.atm_ce_ltp ? `₹${fmt(t.atm_ce_ltp)}` : "—"}
        </td>
        <td className="px-4 py-2 text-right font-mono-data">
          {closeLtp ? `₹${fmt(closeLtp)}` : "—"}
        </td>
        <td className="px-4 py-2 text-right" style={{ color }}>
          <span className="font-mono-data">
            {pnl == null ? "—" : `${pnl >= 0 ? "+" : ""}${fmt(pnl)}%`}
          </span>
        </td>
        <td className="px-4 py-2 text-right">
          <span
            className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 border font-mono-data"
            style={{
              color: closed
                ? (t.close_reason === "PROFIT_BOOKED" ? "var(--bull)" : "var(--text-muted)")
                : "var(--bull)",
              borderColor: closed
                ? (t.close_reason === "PROFIT_BOOKED" ? "var(--bull)" : "var(--border-color)")
                : "var(--bull)",
            }}
            title={t.sell_skipped_reason ? `Sell skipped — ${t.sell_skipped_reason}` : (t.close_reason || "")}
          >
            {(() => {
              if (!closed) return "OPEN";
              if (t.close_reason === "PROFIT_BOOKED") return "BOOKED";
              if (t.close_reason === "STOP_LOSS") return "STOP LOSS";
              if (t.close_reason === "MULTI_TF_REVERSAL") return "MTF REVERSAL";
              if (t.close_reason === "MANUAL_EXIT") {
                if (t.live_trade && t.live_sell_order_id && !t.live_sell_error) return "SOLD";
                return "MANUAL EXIT";
              }
              if (t.close_reason === "EOD") return "EOD CLOSE";
              if (t.close_reason === "BUY_CHASE_TIMEOUT") return "BUY TIMEOUT";
              return "CLOSED";
            })()}
          </span>
        </td>
        <td className="px-4 py-2 text-right">
          {!closed && t.id ? (
            <button
              data-testid={`manual-exit-${day}-${i}`}
              onClick={() => onManualExit && onManualExit(t.id, t.symbol)}
              className="text-[10px] uppercase tracking-wider font-mono-data px-2.5 py-1 border hover:bg-white/5"
              style={{ color: "var(--text-primary)", borderColor: "var(--border-color)" }}
              title={t.live_trade ? "Manual Exit — validates position then places SELL on Upstox" : "Manual Exit — paper close at current LTP"}
            >
              Exit
            </button>
          ) : (
            <span className="text-muted-3 text-[10px]">—</span>
          )}
        </td>
      </tr>
      {expanded && <ExecutionJourney trade={t} colSpan={9} />}
    </React.Fragment>
  );
};


const PaperTradeHistory = ({ trades, open, onToggle, onManualExit }) => {
  const today = trades?.today || [];
  const earlierAll = trades?.earlier || [];
  // 2026-09-18 — the backend already retains PAPER_TRADE_RETENTION_DAYS
  // (14) days of trades and returns it as `retention_days`; the UI was just
  // lumping every non-today trade into one flat "Yesterday" bucket and
  // hardcoding "last 2 days" in the header, so 2 weeks of history read as
  // 1 day (chat: "Paper trade only for 2 days? can we display more like 2
  // weeks?"). Fix is display-only: group `earlier` by its real calendar day
  // and show the actual retention window.
  const retentionDays = trades?.retention_days ?? 14;
  const total = today.length + earlierAll.length;

  const earlierByDay = useMemo(() => {
    const map = new Map();
    for (const t of earlierAll) {
      const key = t.day || "unknown";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(t);
    }
    // Newest day first.
    return Array.from(map.entries()).sort((a, b) => (a[0] < b[0] ? 1 : -1));
  }, [earlierAll]);

  const dayLabel = (dayStr) => {
    if (!dayStr || dayStr === "unknown") return "Earlier";
    try {
      const nowIst = new Date(
        new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" })
      );
      const y = new Date(nowIst);
      y.setDate(y.getDate() - 1);
      const yesterdayStr = `${y.getFullYear()}-${String(y.getMonth() + 1).padStart(2, "0")}-${String(y.getDate()).padStart(2, "0")}`;
      if (dayStr === yesterdayStr) return "Yesterday";
      const d = new Date(`${dayStr}T00:00:00+05:30`);
      if (isNaN(d.getTime())) return dayStr;
      return new Intl.DateTimeFormat("en-GB", {
        day: "2-digit", month: "short", year: "numeric",
      }).format(d);
    } catch {
      return dayStr;
    }
  };

  const groups = [
    { label: "Today", items: today, testid: "paper-history-today", slug: "today" },
    ...earlierByDay.map(([day, items]) => ({
      label: dayLabel(day),
      items,
      testid: `paper-history-day-${day}`,
      slug: day,
    })),
  ];

  return (
    <div className="border-grid surface" data-testid="paper-history-section">
      <div className="w-full flex items-center justify-between px-4 py-2.5">
        <button
          data-testid="paper-history-toggle"
          onClick={onToggle}
          aria-expanded={open}
          className="flex-1 flex items-center justify-between hover:bg-white/[0.02] -my-2.5 py-2.5"
        >
          <span className="text-[11px] uppercase tracking-wider font-mono-data text-muted-2 inline-flex items-center gap-2">
            <History size={13} />
            Paper Trade History
            <span className="text-muted-3">· last {retentionDays} days · {total} trades</span>
          </span>
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>
        <a
          href={`${API}/candle-breakout/export.csv`}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="candle-breakout-export-csv"
          onClick={(e) => e.stopPropagation()}
          className="ml-3 inline-flex items-center gap-1.5 px-2 py-1 border border-grid text-[10px] uppercase tracking-[0.2em] font-mono-data hover:text-white"
          title="Export all closed trades (full data) as CSV"
        >
          <Download size={12} /> Export CSV
        </a>
      </div>

      {open && (
        <div className="border-t border-grid">
          {total === 0 ? (
            <div className="px-4 py-4 text-xs text-muted-2 text-center">
              No paper trades yet. Click "Mark" on any pick above to start
              tracking. Trades auto-expire after {retentionDays} days.
            </div>
          ) : (
            <div className="space-y-3 pb-3">
              {groups.map((g) => (
                g.items.length === 0 ? null : (
                  <div key={g.label}>
                    <div className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-wider text-muted-3 font-mono-data">
                      {g.label} · {g.items.length}
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs" data-testid={g.testid}>
                        <thead>
                          <tr className="border-b border-grid text-[10px] uppercase tracking-wider text-muted-3 font-mono-data">
                            <th className="text-left px-4 py-2 w-12">Sr.</th>
                            <th className="text-left px-4 py-2">Entry IST</th>
                            <th className="text-left px-4 py-2">Symbol</th>
                            <th className="text-right px-4 py-2">Strike</th>
                            <th className="text-right px-4 py-2">Entry LTP</th>
                            <th className="text-right px-4 py-2">Current LTP</th>
                            <th className="text-right px-4 py-2">P&amp;L</th>
                            <th className="text-right px-4 py-2">Status</th>
                            <th className="text-right px-4 py-2 w-32">Manual Exit</th>
                          </tr>
                        </thead>
                        <tbody>
                          {g.items.map((t, i) => (
                            <PaperHistoryRow key={t.slot + "_" + t.symbol + "_" + i} t={t} i={i} day={g.slug} onManualExit={onManualExit} />
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

